// src/Studio/masks/maskPathUtils.ts

import type { MaskPath, MaskPoint } from '../types/gradientFade';

/**
 * Convert MaskPath to SVG path string for rendering
 */
export function maskPathToSvgPath(path: MaskPath): string {
  if (path.points.length === 0) return '';

  const commands: string[] = [];

  path.points.forEach((pt, i) => {
    if (i === 0) {
      commands.push(`M ${pt.x} ${pt.y}`);
    } else {
      const prev = path.points[i - 1];

      // Check if we have bezier control points
      if (prev.cpOut || pt.cpIn) {
        const cp1 = prev.cpOut || { x: prev.x, y: prev.y };
        const cp2 = pt.cpIn || { x: pt.x, y: pt.y };
        commands.push(`C ${cp1.x} ${cp1.y} ${cp2.x} ${cp2.y} ${pt.x} ${pt.y}`);
      } else {
        commands.push(`L ${pt.x} ${pt.y}`);
      }
    }
  });

  if (path.closed && path.points.length > 2) {
    // Close with curve if needed
    const last = path.points[path.points.length - 1];
    const first = path.points[0];

    if (last.cpOut || first.cpIn) {
      const cp1 = last.cpOut || { x: last.x, y: last.y };
      const cp2 = first.cpIn || { x: first.x, y: first.y };
      commands.push(`C ${cp1.x} ${cp1.y} ${cp2.x} ${cp2.y} ${first.x} ${first.y}`);
    }
    commands.push('Z');
  }

  return commands.join(' ');
}

/**
 * Draw a MaskPath onto a canvas context
 */
export function drawMaskPathToContext(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  path: MaskPath
): void {
  if (path.points.length === 0) return;

  ctx.beginPath();

  path.points.forEach((pt, i) => {
    if (i === 0) {
      ctx.moveTo(pt.x, pt.y);
    } else {
      const prev = path.points[i - 1];

      if (prev.cpOut || pt.cpIn) {
        const cp1 = prev.cpOut || { x: prev.x, y: prev.y };
        const cp2 = pt.cpIn || { x: pt.x, y: pt.y };
        ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, pt.x, pt.y);
      } else {
        ctx.lineTo(pt.x, pt.y);
      }
    }
  });

  if (path.closed && path.points.length > 2) {
    const last = path.points[path.points.length - 1];
    const first = path.points[0];

    if (last.cpOut || first.cpIn) {
      const cp1 = last.cpOut || { x: last.x, y: last.y };
      const cp2 = first.cpIn || { x: first.x, y: first.y };
      ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, first.x, first.y);
    }
    ctx.closePath();
  }
}

/**
 * Hit-test a point against a MaskPath
 * Returns the index of the point if within threshold, or -1
 */
export function hitTestMaskPoint(
  path: MaskPath,
  x: number,
  y: number,
  threshold: number = 8
): number {
  for (let i = 0; i < path.points.length; i++) {
    const pt = path.points[i];
    const dx = pt.x - x;
    const dy = pt.y - y;
    if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
      return i;
    }
  }
  return -1;
}

/**
 * Hit-test a point against a bezier control handle
 * Returns { pathIndex, handleType } or null
 */
export function hitTestControlHandle(
  path: MaskPath,
  x: number,
  y: number,
  threshold: number = 6
): { pointIndex: number; handleType: 'cpIn' | 'cpOut' } | null {
  for (let i = 0; i < path.points.length; i++) {
    const pt = path.points[i];

    if (pt.cpIn) {
      const dx = pt.cpIn.x - x;
      const dy = pt.cpIn.y - y;
      if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
        return { pointIndex: i, handleType: 'cpIn' };
      }
    }

    if (pt.cpOut) {
      const dx = pt.cpOut.x - x;
      const dy = pt.cpOut.y - y;
      if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
        return { pointIndex: i, handleType: 'cpOut' };
      }
    }
  }
  return null;
}

/**
 * Check if a point is inside a closed MaskPath
 * Uses ray casting algorithm
 */
export function isPointInsidePath(path: MaskPath, x: number, y: number): boolean {
  if (!path.closed || path.points.length < 3) return false;

  let inside = false;
  const pts = path.points;

  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Simplify a path by removing points that are too close together
 * Useful for lasso tool to reduce point count
 */
export function simplifyPath(path: MaskPath, minDistance: number = 3): MaskPath {
  if (path.points.length < 3) return path;

  const simplified: MaskPoint[] = [path.points[0]];

  for (let i = 1; i < path.points.length; i++) {
    const last = simplified[simplified.length - 1];
    const curr = path.points[i];
    const dx = curr.x - last.x;
    const dy = curr.y - last.y;

    if (Math.sqrt(dx * dx + dy * dy) >= minDistance) {
      simplified.push(curr);
    }
  }

  // Always keep the last point if path is not closed
  if (!path.closed && simplified[simplified.length - 1] !== path.points[path.points.length - 1]) {
    simplified.push(path.points[path.points.length - 1]);
  }

  return { ...path, points: simplified };
}

/**
 * Get bounding box of a MaskPath
 */
export function getPathBounds(path: MaskPath): { x: number; y: number; width: number; height: number } {
  if (path.points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  for (const pt of path.points) {
    minX = Math.min(minX, pt.x);
    minY = Math.min(minY, pt.y);
    maxX = Math.max(maxX, pt.x);
    maxY = Math.max(maxY, pt.y);

    // Include control points in bounds
    if (pt.cpIn) {
      minX = Math.min(minX, pt.cpIn.x);
      minY = Math.min(minY, pt.cpIn.y);
      maxX = Math.max(maxX, pt.cpIn.x);
      maxY = Math.max(maxY, pt.cpIn.y);
    }
    if (pt.cpOut) {
      minX = Math.min(minX, pt.cpOut.x);
      minY = Math.min(minY, pt.cpOut.y);
      maxX = Math.max(maxX, pt.cpOut.x);
      maxY = Math.max(maxY, pt.cpOut.y);
    }
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Offset/expand a path by a given number of pixels
 * Positive = expand outward, negative = contract inward
 * Uses simple vertex offset (not true polygon offset)
 */
export function offsetPath(path: MaskPath, offsetPx: number): MaskPath {
  if (offsetPx === 0 || path.points.length < 3 || !path.closed) {
    return path;
  }

  const offsetPoints: MaskPoint[] = [];
  const pts = path.points;
  const n = pts.length;

  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const curr = pts[i];
    const next = pts[(i + 1) % n];

    // Calculate edge vectors
    const v1x = curr.x - prev.x;
    const v1y = curr.y - prev.y;
    const v2x = next.x - curr.x;
    const v2y = next.y - curr.y;

    // Calculate normals (perpendicular to edges, pointing outward)
    const len1 = Math.sqrt(v1x * v1x + v1y * v1y) || 1;
    const len2 = Math.sqrt(v2x * v2x + v2y * v2y) || 1;

    const n1x = -v1y / len1;
    const n1y = v1x / len1;
    const n2x = -v2y / len2;
    const n2y = v2x / len2;

    // Average the normals for smooth offset at corners
    let nx = (n1x + n2x) / 2;
    let ny = (n1y + n2y) / 2;
    const nlen = Math.sqrt(nx * nx + ny * ny) || 1;
    nx /= nlen;
    ny /= nlen;

    // Scale factor for sharp corners
    const dot = n1x * n2x + n1y * n2y;
    const scale = 1 / Math.max(0.5, (1 + dot) / 2);

    offsetPoints.push({
      x: curr.x + nx * offsetPx * scale,
      y: curr.y + ny * offsetPx * scale,
      // Don't offset control points for now
    });
  }

  return { ...path, points: offsetPoints };
}
